import type { RequestAttachment, RequestMessage, TimelineStep } from './demandes'

export type PermitStatus = 'depose' | 'analyse' | 'approuve' | 'emis'

export const permitStatusLabels: Record<PermitStatus, string> = {
  depose: 'Déposée',
  analyse: 'En analyse',
  approuve: 'Acceptée',
  emis: 'Permis émis',
}

export const permitStatusOrder: PermitStatus[] = ['depose', 'analyse', 'approuve', 'emis']

/**
 * Les frais ne sont exigibles qu'une fois la demande acceptée par la Ville :
 * « a-venir » avant la décision, « a-payer » dès l'acceptation, « paye » ensuite.
 */
export type FeeStatus = 'a-venir' | 'a-payer' | 'paye'

export const feeStatusLabels: Record<FeeStatus, string> = {
  'a-venir': 'Payables à l’acceptation',
  'a-payer': 'À payer',
  paye: 'Payés',
}

/** Adresse de la propriété au profil, reprise par ATNA. */
export const profileAddressLabel = '366, chemin Lakeshore'

/** Document exigé au dossier d'une demande de permis. */
export interface PermitDocument {
  id: string
  name: string
  detail: string
  received: boolean
}

export interface PermitApplication {
  id: string
  reference: string
  title: string
  /** Service municipal responsable (ex. Urbanisme). */
  service: string
  /** Catégorie réglementaire du permis. */
  category: string
  address: string
  fullAddress: string
  submittedOn: string
  createdOn: string
  decisionOn: string | null
  createdBy: string
  status: PermitStatus
  description: string
  /** Frais exigibles, déjà formatés. */
  fee: string
  feeStatus: FeeStatus
  /** Échéance de paiement, une fois la demande acceptée. */
  feeDueOn?: string
  /** Délai de traitement annoncé par la Ville. */
  processingTime: string
  documents: PermitDocument[]
  attachments: RequestAttachment[]
  timeline: TimelineStep[]
  messages: RequestMessage[]
}

/** Types de permis les plus demandés, présentés dans le catalogue. */
export interface PermitType {
  id: string
  name: string
  detail: string
  fee: string
  processingTime: string
  /** Sujet envoyé à ATNA quand le citoyen choisit ce permis. */
  prompt: string
}

export const permitTypes: PermitType[] = [
  {
    id: 'piscine',
    name: 'Piscine ou spa',
    detail: 'Creusée, hors-terre ou démontable de plus de 60 cm',
    fee: '145,00 $',
    processingTime: '15 jours ouvrables',
    prompt: 'Je veux construire une piscine dans ma cour',
  },
  {
    id: 'cloture',
    name: 'Clôture, haie ou muret',
    detail: 'Installation, remplacement ou modification de hauteur',
    fee: '65,00 $',
    processingTime: '10 jours ouvrables',
    prompt: 'Installer une clôture dans ma cour arrière',
  },
  {
    id: 'renovation',
    name: 'Rénovation intérieure',
    detail: 'Cuisine, salle de bain, sous-sol, structure',
    fee: '110,00 $',
    processingTime: '15 jours ouvrables',
    prompt: 'Rénover ma salle de bain',
  },
  {
    id: 'agrandissement',
    name: 'Agrandissement ou nouvelle construction',
    detail: 'Ajout d’étage, rallonge, garage, cabanon de plus de 15 m²',
    fee: '310,00 $',
    processingTime: '30 jours ouvrables',
    prompt: 'Agrandir ma maison à l’arrière',
  },
  {
    id: 'arbre',
    name: 'Abattage d’arbre',
    detail: 'Arbre de plus de 10 cm de diamètre sur terrain privé',
    fee: 'Gratuit',
    processingTime: '10 jours ouvrables',
    prompt: 'Abattre un arbre dans ma cour',
  },
  {
    id: 'stationnement',
    name: 'Entrée charretière ou stationnement',
    detail: 'Nouvelle entrée, élargissement, pavage',
    fee: '90,00 $',
    processingTime: '15 jours ouvrables',
    prompt: 'Élargir mon entrée de stationnement',
  },
]

/** Puces contextuelles sous la barre ATNA du module Permis. */
export const permitQuickPrompts = [
  'Construire une piscine',
  'Installer une clôture',
  'Rénover ma salle de bain',
  'Abattre un arbre',
  'Agrandir ma maison',
]

export const permitDetailQuestions = [
  'Où en est ma demande de permis ?',
  'Quels documents manquent ?',
  'Puis-je commencer les travaux ?',
]

export const permitApplications: PermitApplication[] = [
  {
    id: 'per-1187',
    reference: '#PER-1187',
    title: 'Rénovation de salle de bain',
    service: 'Urbanisme et permis',
    category: 'Rénovation intérieure',
    address: '366, chemin Lakeshore',
    fullAddress: '366, chemin Lakeshore, Beaconsfield, Québec, H9W 4S4',
    submittedOn: 'Déposée le 18 septembre',
    createdOn: '18/09/26',
    decisionOn: null,
    createdBy: 'ATNA',
    status: 'analyse',
    description:
      'Réfection complète de la salle de bain du premier étage : remplacement de la baignoire par une douche, nouveau revêtement de plancher et déplacement du lavabo. Aucune modification à la structure porteuse.',
    fee: '110,00 $',
    feeStatus: 'a-venir',
    processingTime: '15 jours ouvrables',
    documents: [
      { id: 'plan', name: 'Plan d’aménagement', detail: 'Croquis coté de la pièce', received: true },
      { id: 'devis', name: 'Devis de l’entrepreneur', detail: 'Licence RBQ incluse', received: true },
      {
        id: 'plomberie',
        name: 'Fiche de plomberie',
        detail: 'Exigée pour le déplacement du lavabo',
        received: false,
      },
    ],
    attachments: [
      { name: 'plan-salle-de-bain.pdf', type: 'pdf', size: '640 Ko' },
      { name: 'devis-entrepreneur.pdf', type: 'pdf', size: '288 Ko' },
    ],
    timeline: [
      { date: '18 septembre', label: 'Demande déposée' },
      { date: '22 septembre', label: 'En analyse par l’inspectrice' },
    ],
    messages: [
      {
        id: 'msg-1187-1',
        author: 'ville',
        authorName: 'Urbanisme et permis',
        sentAt: '22 septembre, 11 h 10',
        body: 'Bonjour, votre dossier est en analyse. Il manque la fiche de plomberie pour le déplacement du lavabo. Vous pouvez la remplir et nous la retourner ici.',
        attachment: { name: 'fiche-plomberie.pdf', type: 'form', size: '156 Ko' },
        awaiting: 'document',
      },
    ],
  },
  {
    id: 'per-1104',
    reference: '#PER-1104',
    title: 'Abattage d’un frêne',
    service: 'Urbanisme et permis',
    category: 'Abattage d’arbre',
    address: '366, chemin Lakeshore',
    fullAddress: '366, chemin Lakeshore, Beaconsfield, Québec, H9W 4S4',
    submittedOn: 'Déposée le 31 juillet',
    createdOn: '31/07/26',
    decisionOn: '09/08/26',
    createdBy: 'Marianne Caldwell',
    status: 'emis',
    description:
      'Frêne atteint de l’agrile situé dans la cour arrière, à environ 3 m de la limite de propriété. Abattage recommandé par un arboriculteur certifié.',
    fee: 'Gratuit',
    feeStatus: 'paye',
    processingTime: '10 jours ouvrables',
    documents: [
      { id: 'expertise', name: 'Expertise d’arboriculteur', detail: 'Diagnostic agrile du frêne', received: true },
      { id: 'photos', name: 'Photos de l’arbre', detail: 'Vue d’ensemble et tronc', received: true },
    ],
    attachments: [{ name: 'expertise-arboriculteur.pdf', type: 'pdf', size: '410 Ko' }],
    timeline: [
      { date: '31 juillet', label: 'Demande déposée' },
      { date: '5 août', label: 'Inspection du terrain' },
      { date: '9 août', label: 'Permis approuvé' },
    ],
    messages: [
      {
        id: 'msg-1104-1',
        author: 'ville',
        authorName: 'Urbanisme et permis',
        sentAt: '9 août, 15 h 05',
        body: 'Le permis d’abattage est approuvé. Un arbre de remplacement doit être planté avant le 1er juin prochain. Le certificat est joint à votre dossier.',
        attachment: { name: 'permis-abattage-1104.pdf', type: 'pdf', size: '198 Ko' },
      },
    ],
  },
  {
    id: 'per-1152',
    reference: '#PER-1152',
    title: 'Clôture de cour arrière',
    service: 'Urbanisme et permis',
    category: 'Clôture, haie ou muret',
    address: '366, chemin Lakeshore',
    fullAddress: '366, chemin Lakeshore, Beaconsfield, Québec, H9W 4S4',
    submittedOn: 'Déposée le 9 septembre',
    createdOn: '09/09/26',
    decisionOn: '22/09/26',
    createdBy: 'ATNA',
    status: 'approuve',
    description:
      'Installation d’une clôture d’intimité en aluminium de 1,8 m le long de la limite arrière du terrain, sur 18 m linéaires, avec portillon donnant sur la ruelle.',
    fee: '65,00 $',
    feeStatus: 'a-payer',
    feeDueOn: '22 octobre 2026',
    processingTime: '10 jours ouvrables',
    documents: [
      { id: 'croquis', name: 'Croquis d’implantation', detail: 'Tracé et hauteur de la clôture', received: true },
      { id: 'certificat', name: 'Certificat de localisation', detail: 'Déjà au dossier de la Ville', received: true },
    ],
    attachments: [{ name: 'croquis-cloture.pdf', type: 'pdf', size: '340 Ko' }],
    timeline: [
      { date: '9 septembre', label: 'Demande déposée par ATNA' },
      { date: '15 septembre', label: 'En analyse par l’inspecteur' },
      { date: '22 septembre', label: 'Demande acceptée — frais exigibles' },
    ],
    messages: [
      {
        id: 'msg-1152-1',
        author: 'ville',
        authorName: 'Urbanisme et permis',
        sentAt: '22 septembre, 10 h 25',
        body: 'Bonne nouvelle : votre demande est acceptée. Le permis sera émis dès le paiement des frais de 65 $. Vous avez 30 jours pour acquitter le montant, en ligne ou au comptoir.',
        awaiting: 'info',
      },
    ],
  },
]

/** Règles vérifiées par ATNA pour une piscine résidentielle. */
export const poolRules = [
  {
    id: 'cloture',
    label: 'Enceinte d’au moins 1,2 m',
    detail: 'Clôture ou paroi empêchant l’accès, avec portillon à fermeture automatique.',
  },
  {
    id: 'limites',
    label: 'Recul de 1,5 m des limites de terrain',
    detail: 'Mesuré depuis la paroi de la piscine, incluant le trottoir de béton.',
  },
  {
    id: 'fils',
    label: 'Aucun fil électrique au-dessus',
    detail: 'Dégagement minimal de 3 m par rapport aux fils aériens.',
  },
  {
    id: 'plongeoir',
    label: 'Profondeur conforme si plongeoir',
    detail: 'Minimum de 3 m sous le tremplin, sinon le plongeoir est refusé.',
  },
]

/** Documents demandés pour un permis de piscine. */
export const poolDocuments = [
  {
    id: 'certificat',
    name: 'Certificat de localisation',
    detail: 'Déjà au dossier de la Ville depuis l’achat de la propriété',
    received: true,
  },
  {
    id: 'implantation',
    name: 'Plan d’implantation',
    detail: 'Position de la piscine, du filtreur et de l’enceinte, avec distances',
    received: false,
  },
  {
    id: 'modele',
    name: 'Fiche du modèle',
    detail: 'Dimensions et profondeur fournies par le détaillant',
    received: false,
  },
]

export const poolPermit = {
  type: 'Piscine creusée',
  fee: '145,00 $',
  processingTime: '15 jours ouvrables',
  reference: '#PER-1204',
}

/** Demande créée lorsque le flux ATNA de permis de piscine aboutit. */
export const poolApplication: PermitApplication = {
  id: 'per-1204',
  reference: '#PER-1204',
  title: 'Construction d’une piscine creusée',
  service: 'Urbanisme et permis',
  category: 'Piscine ou spa',
  address: '366, chemin Lakeshore',
  fullAddress: '366, chemin Lakeshore, Beaconsfield, Québec, H9W 4S4',
  submittedOn: 'Déposée aujourd’hui',
  createdOn: '06/10/26',
  decisionOn: null,
  createdBy: 'ATNA',
  status: 'depose',
  description:
    'Installation d’une piscine creusée de 4,5 m sur 9 m dans la cour arrière, avec enceinte de 1,4 m et portillon à fermeture automatique. Recul de 1,8 m des limites de terrain, aucun fil aérien au-dessus de l’emplacement prévu.',
  fee: '145,00 $',
  feeStatus: 'a-venir',
  processingTime: '15 jours ouvrables',
  documents: poolDocuments.map((document) => ({ ...document, received: true })),
  attachments: [
    { name: 'plan-implantation-piscine.pdf', type: 'pdf', size: '520 Ko' },
    { name: 'fiche-modele-piscine.pdf', type: 'pdf', size: '180 Ko' },
  ],
  timeline: [
    { date: 'Aujourd’hui', label: 'Demande déposée par ATNA' },
    { date: 'Aujourd’hui', label: 'Aucun frais exigible avant l’acceptation' },
  ],
  messages: [
    {
      id: 'msg-1204-1',
      author: 'ville',
      authorName: 'Urbanisme et permis',
      sentAt: 'Aujourd’hui',
      body: 'Nous avons reçu votre demande de permis de piscine. Une inspectrice valide les distances et l’enceinte dans les 15 jours ouvrables. Les frais de 145 $ ne seront exigibles qu’à l’acceptation de la demande, et les travaux ne peuvent commencer avant l’émission du permis.',
    },
  ],
}

/** Modes de paiement offerts une fois la demande acceptée. */
export const permitCashSteps = [
  {
    title: 'Présentez votre code de paiement',
    detail: 'Le code du dossier est rattaché à votre demande de permis.',
  },
  {
    title: 'Rendez-vous au comptoir de l’urbanisme',
    detail: 'Hôtel de ville, 303, boulevard Beaconsfield · lundi au jeudi, 8 h à 16 h 45 ; vendredi, 8 h à 12 h.',
  },
  {
    title: 'Acquittez les frais',
    detail: 'Argent comptant, débit ou chèque à l’ordre de la Ville de Beaconsfield.',
  },
  {
    title: 'Recevez votre permis',
    detail: 'Le certificat est remis sur place et déposé dans votre dossier en ligne.',
  },
]

/** Formulaire classique : permis d'exploitation d'un véhicule de restauration. */
export const foodTruckMenuTypes = [
  'Cuisine de rue (burgers, frites, poutine)',
  'Cuisine du monde',
  'Desserts et crème glacée',
  'Café et boissons',
]

export const foodTruckZones = [
  { id: 'centennial', label: 'Parc Centennial', detail: 'Zone autorisée · 6 emplacements' },
  { id: 'windermere', label: 'Parc Windermere', detail: 'Zone autorisée · 4 emplacements' },
  {
    id: 'hotel-de-ville',
    label: 'Place de l’Hôtel-de-Ville',
    detail: 'Événements municipaux seulement',
  },
  { id: 'christmas', label: 'Parc Christmas', detail: 'Zone autorisée · 2 emplacements' },
]

export const foodTruckDays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']

export const foodTruckDocuments = [
  { id: 'mapaq', name: 'Permis du MAPAQ', detail: 'Restauration mobile, en vigueur' },
  { id: 'immatriculation', name: 'Certificat d’immatriculation du véhicule', detail: 'SAAQ' },
  { id: 'assurance', name: 'Assurance responsabilité de 2 M$', detail: 'Attestation de l’assureur' },
  { id: 'propane', name: 'Attestation d’installation au propane', detail: 'Émise par un technicien certifié' },
  { id: 'plan', name: 'Plan d’aménagement du véhicule', detail: 'Équipements de cuisson et extincteurs' },
]

/** Demande créée à l'envoi du formulaire classique. */
export const foodTruckApplication: PermitApplication = {
  id: 'per-1310',
  reference: '#PER-1310',
  title: 'Exploitation d’un véhicule de restauration',
  service: 'Urbanisme et permis',
  category: 'Commerce mobile (food truck)',
  address: 'Parc Centennial',
  fullAddress: '288, boulevard Beaconsfield, Beaconsfield, Québec, H9W 4A7',
  submittedOn: 'Déposée aujourd’hui',
  createdOn: '06/10/26',
  decisionOn: null,
  createdBy: 'Formulaire en ligne',
  status: 'depose',
  description:
    'Demande de permis d’exploitation pour un véhicule de restauration mobile de 7,3 m, cuisine de rue, exploité du jeudi au dimanche entre 11 h et 20 h dans les zones autorisées du parc Centennial et du parc Windermere.',
  fee: '425,00 $',
  feeStatus: 'a-venir',
  processingTime: '30 jours ouvrables',
  documents: foodTruckDocuments.map((document) => ({ ...document, received: true })),
  attachments: [
    { name: 'permis-mapaq.pdf', type: 'pdf', size: '220 Ko' },
    { name: 'assurance-responsabilite.pdf', type: 'pdf', size: '310 Ko' },
    { name: 'plan-amenagement-vehicule.pdf', type: 'pdf', size: '760 Ko' },
  ],
  timeline: [
    { date: 'Aujourd’hui', label: 'Formulaire reçu' },
    { date: 'Aujourd’hui', label: 'Vérification de l’admissibilité en cours' },
  ],
  messages: [
    {
      id: 'msg-1310-1',
      author: 'ville',
      authorName: 'Urbanisme et permis',
      sentAt: 'Aujourd’hui',
      body: 'Votre formulaire a été reçu. L’analyse porte sur les zones demandées et la conformité du véhicule. Les frais de 425 $ seront exigibles seulement si la demande est acceptée.',
    },
  ],
}
